import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { repeat } from 'lit-html/directives/repeat.js';
import { guard } from 'lit-html/directives/guard.js';
import { cache } from 'lit-html/directives/cache.js';
import { ifNotNull } from '../../utils/directives';
import { KatLitElement, Keys, register, nextUniqueId } from '../../shared/base';
import { formInputMap } from '../../utils/form-input-map';
import { fitHandler } from '../dropdown/fit-handler';
import baseStyles from '../../shared/base/base.lit.scss';
import formItemStyles from '../../shared/base/form-item.base.lit.scss';
import { debounce } from '../../utils/debounce';
import styles from './predictive-input.lit.scss';
import optionStyles from './option.lit.scss';

type OptionsFilter = (element: string, index: number, array: []) => boolean;

/**
 * @component {kat-predictive-input} KatalPredictiveinput A predictive input allows users to enter text and surfaces options that match the text the user has input from a finite pre-defined list of options.
 * @guideline Do Stringify data objects when passing them to attribute data
 * @example Basic {"value": "Test", "label":"Predictive Input", "options":["Test","Test Name","Katal","CXD","Flo"] }
 * @example WithTooltip {"value": "Test", "label":"Predictive Input",
 * "options":["Test","Test Name","Katal","CXD","Flo"],
 * "tooltip-text": "Tooltip content"}
 * @example withJSON {"label": "Predictive input with JSON options", "value": "testValue",
 * "options": [{"name":"testName","value":"testValue"},{"name":"testName2","value":"testValue2"}]}
 * @event {{ value: string }} change This event fires when focus on input is gone AND value is different. Detail object contains the current input value.
 * @event {{ value: string }} blur This event fires whenever the inner-input element fires blur. Detail object contains the inner-input value BEFORE focus is gone.
 * @event mouseup This event fires whenever the inner-input element fires mouseup. Detail object is unavailable for this event.
 * @event input This event fires whenever the inner-input element fires input. Detail object contains current input value.
 * @event {{ value: string }} focus This event fires whenever the inner-input element fires focus. Detail object contains the current input value.
 * @event keydown This event fires whenever the inner-input element fires keydown. Detail object is unavailable for this event.
 * @event expand This event fires whenever the options are expanded.
 * @event collapse This event fires whenever the options are collapsed.
 * @theme flo
 * @status Production
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-predictive-input')
export class KatPredictiveInput extends KatLitElement {
  _customOptionsFilter?: OptionsFilter;

  /** Specifies the max length of characters allowed in the input */
  @property()
  maxLength?: number;

  /** Specifies that the element is read only */
  @property()
  readOnly?: boolean;

  /**
   * Specifies that the element should receive focus by default
   * @required false
   */
  @property()
  autofocus: boolean;

  /** Specifies that the element should receive focus by default */
  @property()
  required?: boolean;

  /**
   * Sets how the list will be filtered
   * @enum {value} contains Will filter if the result contains the criteria
   * @enum {value} startsWith Will filter if the result starts with the criteria
   */
  @property({ attribute: 'filter-type' })
  filterType = 'contains';

  /** Array of `Option` strings, e.g. ["test","value","testValue","icon"] */
  @property()
  options: string[] = [];

  /** The text that will be displayed to the user as a placeholder. */
  @property()
  placeholder?: string;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /** The optional field label. */
  @property()
  label?: string;

  /**
   * Label read by assistive technology.
   */
  @property({ attribute: 'kat-aria-label' })
  katAriaLabel?: string;

  /** The optional field name, hidden from view. */
  @property()
  name?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean;

  /** The value of the selected option. */
  @property()
  value?: string;

  /**
   * The size of the selected option.
   * @enum {value} large Large select box - Default
   * @enum {value} small Small select box
   */
  @property()
  size = 'large';

  /** Setting the select box to disabled will stop its interactivity. */
  @property()
  disabled?: boolean;

  /** Used to associate an external label to an input.  See the kat-label for attribute. */
  @property({ attribute: 'unique-id' })
  uniqueId: string = nextUniqueId();

  /** Optional pretext for the constraint label.  Used to provide addtional context to the constraint for the input */
  @property({ attribute: 'constraint-emphasis' })
  constraintEmphasis?: string;

  /** Provides users with more information about what they enter into the input */
  @property({ attribute: 'constraint-label' })
  constraintLabel?: string;

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
   */
  @property()
  state?: string;

  /** Optional pretext for the state label.  Used to provide additional context to the state for the input */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state its in */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /**
   * Specifies the autocomplete behavior that a browser should attempt:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @property()
  autocomplete?: string;

  /**
   * The text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  @property({ attribute: 'tooltip-text' })
  tooltipText?: string;

  /** The position of the tooltip. Defaults to "top". */
  @property({ attribute: 'tooltip-position' })
  tooltipPosition?: string;

  /** The icon that triggers the tooltip next to the label. Defaults to "help_outline". */
  @property({ attribute: 'tooltip-trigger-icon' })
  tooltipTriggerIcon?: string;

  /**
   * Controls whether element may be checked for spelling errors. Defaults to false.
   * See https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/spellcheck#security_and_privacy_concerns before enabling.
   */
  @property({ attribute: 'kat-spellcheck' })
  katSpellcheck = false;

  /**
   * A function used to filter dropdown options.
   * @callback OptionsFilter
   * @param {string} element The current element being processed in the array.
   * @param {number} index The index of the current element being processed in the array.
   * @param {string[]} options The options being filtered.
   * @returns {boolean} True to keep the option in the array, false to remove it.
   */

  /**
   * Adds a custom function for filtering options
   *
   * @param {OptionsFilter} filter The function called to filter the dropdown options.
   * @katalmethod
   */
  addCustomFilter(filter: OptionsFilter) {
    this._customOptionsFilter = filter;
  }

  /**
   * Remove custom options filter, falling back to filterType property.
   * @katalmethod
   */
  removeCustomFilter() {
    this._customOptionsFilter = undefined;
  }

  static get styles() {
    return [baseStyles, formItemStyles, optionStyles, styles];
  }

  firstUpdated() {
    this.addEventListener('focusout', e => {
      const container = this.shadowRoot.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (!this.disabled && !container.contains(newFocus)) {
        this.dispatchCustomEvent('blur', {}, false);
        this.toggleExpanded(false);
      }
    });
  }

  @formInputMap([
    {
      tag: 'input',
      name: (component: KatPredictiveInput) => component.name,
      isNeeded: (component: KatPredictiveInput) =>
        component.isFormInputNeeded(),
      setup: (component: KatPredictiveInput, input: HTMLInputElement) =>
        component.setupFormInput(input),
    },
  ])
  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('expanded')) {
      this.setOrUnsetOptionsContainerBottomOffset();
    }
  }

  isFormInputNeeded() {
    return !(this.disabled || !this.name);
  }

  setupFormInput(input: HTMLInputElement) {
    input.value = this.value;
    this.readOnly && input.setAttribute('readonly', 'readonly');
    this.required && input.setAttribute('required', 'required');
  }

  renderOptions() {
    return html`${guard([this.options, this.value], () => {
      const options = this.filterOptions();
      return html`${repeat(
        options,
        item => (typeof item === 'string' ? item : (item as any).name),
        (option, index) => html`
          <div
            class="option"
            part="predictive-input-option-${index}"
            tabindex="-1"
            data-value="${option.name || option}"
          >
            <div class="content">${option.name || option}</div>
            <span class="ring"></span>
          </div>
        `
      )}`;
    })}`;
  }

  render() {
    const formLabel = this.label
      ? html`
          <kat-label
            class="form-label"
            part="predictive-input-label"
            for="${this.uniqueId}"
            text="${ifNotNull(this.label)}"
            .tooltipText=${ifNotNull(this.tooltipText)}
            .tooltipPosition=${ifNotNull(this.tooltipPosition)}
            .tooltipTriggerIcon=${ifNotNull(this.tooltipTriggerIcon)}
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            part="predictive-input-constraint-label"
            for="${this.uniqueId}"
            variant="constraint"
            emphasis="${ifNotNull(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
          ></kat-label>
        `
      : nothing;

    const stateLabel =
      this.stateLabel || this.stateEmphasis
        ? html`
            <kat-label
              class="state-label"
              part="predictive-input-state-label"
              variant="constraint"
              text="${ifNotNull(this.stateLabel)}"
              emphasis="${ifNotNull(this.stateEmphasis)}"
              state="${this.state}"
            ></kat-label>
          `
        : nothing;

    let inputClass = 'header-row-text value ';
    if (this.state) {
      inputClass += this.state;
    }

    const containerClass = 'kat-select-container ' + this.size;

    return html`
      ${formLabel}
      <div class="${containerClass}" @change=${this.selectContainerChange}>
        <div class="select-header">
          <input
            class="${inputClass}"
            part="predictive-input-input"
            id="${this.uniqueId}"
            type="text"
            name="${ifNotNull(this.name)}"
            placeholder="${ifNotNull(this.placeholder)}"
            .value=${ifNotNull(this.value)}
            spellcheck=${this.katSpellcheck}
            maxlength="${ifNotNull(this.maxLength)}"
            autocomplete=${ifNotNull(this.autocomplete)}
            ?disabled="${this.disabled}"
            ?readonly="${this.readOnly}"
            ?autofocus="${this.autofocus}"
            ?required="${this.required}"
            @focus=${this.inputFocus}
            @keydown=${this.inputKeyPress}
            @input=${this.onInput}
            aria-label=${ifNotNull(this.katAriaLabel)}
          />
          <span class="ring"></span>
        </div>
        <div
          class="select-options"
          part="predictive-input-options"
          @click=${this.selectOptionClick}
          @keydown=${this.selectOptionKeydown}
        >
          <div
            class="option-inner-container"
            style="${ifNotNull(
              this.maxHeight &&
                'max-height:' + this.maxHeight + ';overflow:auto;'
            )}"
          >
            ${cache(this.expanded ? this.renderOptions() : nothing)}
          </div>
        </div>
      </div>
      <div class="metadata">${constraintLabel} ${stateLabel}</div>
    `;
  }

  selectContainerChange(e) {
    e.stopPropagation();
    const target = e.target;
    this.value = e.detail ? e.detail.value : target.value;
    this.dispatchCustomEvent('change', { value: this.value });
  }

  selectOptionClick(event) {
    if (!this.disabled) {
      this.selectOption(event.target);
    }
  }

  selectOptionKeydown(evt) {
    const option = evt.target;
    if (this.disabled || !option) return;

    if (evt.keyCode === Keys.Space || evt.keyCode === Keys.Enter) {
      evt.preventDefault();
      this.selectOption(option);
    } else if (evt.keyCode === Keys.ArrowUp) {
      //previous
      evt.preventDefault();
      const prevEl = option.previousElementSibling;
      if (prevEl) {
        prevEl.focus();
      } else {
        const input = this.shadowRoot.querySelector('input');
        // this can never be null
        input.focus();
      }
    } else if (evt.keyCode === Keys.ArrowDown) {
      //next
      evt.preventDefault();
      const nextEl = option.nextElementSibling;
      nextEl?.focus();
    }
  }

  inputKeyPress(e) {
    const keyCode = e.keyCode || e.which;
    if (keyCode === Keys.ArrowDown && this.options.length > 0) {
      this.shadowRoot.querySelector<HTMLElement>('.option').focus();
    }
  }

  @debounce(150)
  updateValue(value) {
    this.value = value;
  }

  onInput(evt) {
    // Stop event and re-emit with the correct value.  Events rely on the underlying values which are being debounced
    if (evt.stopPropagation) evt.stopPropagation();
    this.dispatchCustomEvent('input', { value: evt.target.value });

    this.updateValue(evt.target.value);
  }

  inputFocus(e) {
    const container = this.shadowRoot.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded(true);
    }

    // Although 'focus' is not a custom event, it doesn't bubble up to parent elements.
    // Likewise, we will synthetically fire 'focus' and not have it bubble.
    this.dispatchCustomEvent('focus', { value: this.value }, false);
  }

  async toggleExpanded(newState) {
    if (this.expanded !== newState) {
      this.expanded = newState;
      await this.updateComplete;
      this.dispatchCustomEvent(
        this.expanded ? 'expand' : 'collapse',
        {},
        false
      );
    }
  }

  fitDropDownWindow() {
    const optionContainer =
      this.shadowRoot.querySelector<HTMLElement>('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement =
      this.shadowRoot.querySelector('.select-options');

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset =
        this.shadowRoot.querySelector<HTMLElement>(
          'div.select-header'
        ).offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOption(element) {
    const intialValue = this.value;
    const newValue = element.closest('.option').dataset.value;
    if (intialValue !== newValue) {
      this.value = newValue;
      this.dispatchCustomEvent('change', { value: this.value });
    }
    this.toggleExpanded(false);
    this.shadowRoot.querySelector('input').focus();
  }

  filterOptions() {
    if (this._customOptionsFilter) {
      return this.options.filter(this._customOptionsFilter);
    }

    if (!this.value) {
      return this.options;
    }
    const filter = o => {
      const name = o.name || o;
      return this.filterType === 'startsWith'
        ? name.toLowerCase().startsWith(this.value.toLowerCase())
        : name.toLowerCase().indexOf(this.value.toLowerCase()) !== -1;
    };
    return this.options.filter(filter);
  }
}
